import { useContext, useEffect, useRef, useState } from 'react';
import NotyfContext from '../contexts/NotyfContext.js';
import { readLoadTestSession, removeLoadTestSession, writeLoadTestSession } from '../utils/loadTestSession.js';

const TERMINAL = new Set(['completed', 'failed', 'cancelled']);
const POLL_INTERVAL_MS = 3000;
export const LOAD_TEST_REPORT_ATTEMPTS = 3;
const terminal = (job) => TERMINAL.has(String(job?.status));

export default function useLoadTestFlow(options) {
    const notyf = useContext(NotyfContext);
    const optionsRef = useRef(options);
    optionsRef.current = options;
    const [job, setJob] = useState(null);
    const [finalData, setFinalData] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [cancelling, setCancelling] = useState(false);
    const [loadingReport, setLoadingReport] = useState(false);
    const [restoringSession, setRestoringSession] = useState(false);
    const [reportAttempt, setReportAttempt] = useState(0);
    const [error, setError] = useState('');
    const [monitoringPaused, setMonitoringPaused] = useState(false);
    const [, setClock] = useState(0);
    const jobRef = useRef(null);
    const finalDataRef = useRef(null);
    const mountedRef = useRef(true);
    const generationRef = useRef(0);
    const hydratedKeyRef = useRef(null);
    const pollTimerRef = useRef(null);
    const reportTimerRef = useRef(null);
    const reportRequestRef = useRef(null);
    const pollRequestRef = useRef(null);
    const runningSignalRef = useRef(null);
    const toastRef = useRef(null);
    const running = Boolean(job && !terminal(job) && !monitoringPaused);
    const busy = running || submitting || restoringSession || loadingReport || cancelling;
    const stage = finalData && !loadingReport ? 'result'
        : terminal(job) || loadingReport ? 'analysis' : job || submitting || restoringSession || monitoringPaused ? 'execution' : 'configuration';

    const context = () => ({ generation: generationRef.current, key: optionsRef.current.sessionKey, options: optionsRef.current });
    const current = (ctx) => mountedRef.current && ctx.generation === generationRef.current && ctx.key === optionsRef.current.sessionKey;
    const clearTimers = () => {
        window.clearTimeout(pollTimerRef.current);
        window.clearTimeout(reportTimerRef.current);
        pollTimerRef.current = null;
        reportTimerRef.current = null;
    };
    const dismissToast = () => {
        if (toastRef.current) notyf.dismiss(toastRef.current);
        toastRef.current = null;
    };
    const signalRunning = (value) => {
        if (runningSignalRef.current === value) return;
        runningSignalRef.current = value;
        optionsRef.current.onTestRunningChange?.(value);
    };
    const acceptJob = (nextJob, restoreInputs = false) => {
        jobRef.current = nextJob;
        setJob(nextJob);
        if (restoreInputs) optionsRef.current.onRestoreInputs?.(nextJob);
        signalRunning(!terminal(nextJob));
    };
    const showError = (message) => {
        setError(message);
        notyf.open({ type: 'danger', message, ripple: true, dismissible: true });
    };
    const loadReport = async (terminalJob, ctx = context(), attempt = 1) => {
        if (!current(ctx) || !terminalJob?.id || reportRequestRef.current || finalDataRef.current) return;
        const request = { ctx, id: terminalJob.id };
        reportRequestRef.current = request;
        setLoadingReport(true);
        setReportAttempt(attempt);
        setError('');
        let retryScheduled = false;
        let completedData = null;
        let completedOutcome = null;
        try {
            const response = await ctx.options.getReport(terminalJob.id);
            if (!current(ctx)) return;
            const report = response.data?.report || response.data?.job?.result;
            if (!report || typeof report !== 'object' || Array.isArray(report)) {
                throw new Error('A API retornou um relatório de teste inválido.');
            }
            const data = { ...response.data, job: response.data.job || terminalJob, report };
            if (!terminal(data.job) || String(data.job.id) !== String(terminalJob.id)) {
                throw new Error('A API retornou um relatório de outro teste ou de uma execução ainda em andamento.');
            }
            const outcome = ctx.options.getOutcome(data);
            acceptJob(data.job);
            finalDataRef.current = data;
            setFinalData(data);
            writeLoadTestSession(ctx.key, terminalJob.id, data, ctx.options.compactReport);
            setCancelling(false);
            setError('');
            completedData = data;
            completedOutcome = outcome;
        } catch (requestError) {
            if (!current(ctx)) return;
            const status = requestError.response?.status;
            if ((status === undefined || [404, 408, 409, 429].includes(status) || status >= 500) && attempt < LOAD_TEST_REPORT_ATTEMPTS) {
                retryScheduled = true;
                reportTimerRef.current = window.setTimeout(() => {
                    reportTimerRef.current = null;
                    loadReport(terminalJob, ctx, attempt + 1);
                }, POLL_INTERVAL_MS);
            } else {
                showError(requestError.response?.data?.message || 'O teste terminou, mas o relatório consolidado ainda não está disponível. Tente consultar o relatório novamente.');
            }
        } finally {
            if (reportRequestRef.current === request) reportRequestRef.current = null;
            if (current(ctx) && !retryScheduled) setLoadingReport(false);
        }
        if (completedData && current(ctx)) {
            dismissToast();
            toastRef.current = notyf.open({
                type: completedOutcome.failed ? 'danger' : completedOutcome.passed && !completedOutcome.warnings.length ? 'success' : 'warning',
                message: `${completedOutcome.label}: ${completedOutcome.message}`,
                ripple: true, dismissible: true, duration: 8000,
            });
            optionsRef.current.onTerminal?.(completedData);
        }
    };
    const schedulePoll = (jobId, ctx, delay = POLL_INTERVAL_MS) => {
        if (!current(ctx)) return;
        window.clearTimeout(pollTimerRef.current);
        pollTimerRef.current = window.setTimeout(() => {
            pollTimerRef.current = null;
            pollStatus(jobId, ctx);
        }, delay);
    };
    const pollStatus = async (jobId, ctx = context()) => {
        if (!current(ctx) || pollRequestRef.current) return;
        const request = { ctx, id: jobId };
        pollRequestRef.current = request;
        try {
            const response = await ctx.options.getStatus(jobId);
            if (!current(ctx)) return;
            const nextJob = ctx.options.normalizeJob?.(response.data) || response.data?.job;
            if (String(nextJob?.id) !== String(jobId) || !nextJob?.status) throw new Error('A API retornou um status inválido.');
            // A late status request must not undo a confirmed cancellation.
            if (terminal(jobRef.current)) return;
            acceptJob(nextJob);
            setMonitoringPaused(false);
            setError('');
            if (terminal(nextJob)) {
                setCancelling(false);
                await loadReport(nextJob, ctx);
            } else schedulePoll(jobId, ctx);
        } catch (requestError) {
            if (!current(ctx)) return;
            if ([401, 403, 404].includes(requestError.response?.status)) {
                if (requestError.response.status === 404) {
                    removeLoadTestSession(ctx.key);
                    jobRef.current = null;
                    setJob(null);
                }
                signalRunning(false);
                setMonitoringPaused(requestError.response.status !== 404);
                setCancelling(false);
                showError(requestError.response?.data?.message || 'Não foi possível localizar o teste em andamento.');
            } else {
                setError('O status está temporariamente indisponível. Uma nova tentativa será feita automaticamente.');
                schedulePoll(jobId, ctx);
            }
        } finally {
            if (pollRequestRef.current === request) pollRequestRef.current = null;
        }
    };
    const restore = async (ctx) => {
        const saved = readLoadTestSession(ctx.key);
        if (!saved) {
            setMonitoringPaused(false);
            return;
        }
        const savedData = saved.finalReportData;
        if (savedData?.job?.id && String(savedData.job.id) === String(saved.jobId) && terminal(savedData.job)) {
            acceptJob(savedData.job, true);
            finalDataRef.current = savedData;
            setFinalData(savedData);
            return;
        }
        setRestoringSession(true);
        try {
            const response = await ctx.options.getStatus(saved.jobId);
            if (!current(ctx)) return;
            const restoredJob = ctx.options.normalizeJob?.(response.data) || response.data?.job;
            if (String(restoredJob?.id) !== String(saved.jobId) || !restoredJob?.status) throw new Error('A API retornou uma sessão de teste inválida.');
            acceptJob(restoredJob, true);
            setMonitoringPaused(false);
            if (terminal(restoredJob)) await loadReport(restoredJob, ctx);
            else schedulePoll(restoredJob.id, ctx, 0);
        } catch (requestError) {
            if (!current(ctx)) return;
            if (requestError.response?.status === 404) removeLoadTestSession(ctx.key);
            setMonitoringPaused(requestError.response?.status !== 404);
            showError(requestError.response?.data?.message || 'Não foi possível recuperar a sessão anterior deste teste. Tente novamente.');
        } finally {
            if (current(ctx)) setRestoringSession(false);
        }
    };

    useEffect(() => {
        if (hydratedKeyRef.current !== options.sessionKey) {
            generationRef.current += 1;
            clearTimers();
            reportRequestRef.current = null;
            pollRequestRef.current = null;
            hydratedKeyRef.current = options.sessionKey;
            jobRef.current = null;
            finalDataRef.current = null;
            setJob(null);
            setFinalData(null);
            setError('');
            setLoadingReport(false);
            setSubmitting(false);
            setCancelling(false);
            setRestoringSession(false);
            setMonitoringPaused(false);
            signalRunning(false);
            dismissToast();
            if (options.show && options.enabled) restore(context());
            return;
        }
        if (!options.show || !options.enabled) return;
        if (terminal(jobRef.current) && !finalDataRef.current) {
            if (!reportRequestRef.current && !reportTimerRef.current) loadReport(jobRef.current);
        } else if (!jobRef.current || monitoringPaused) restore(context());
    }, [options.sessionKey, options.show, options.enabled]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!running) return undefined;
        const interval = window.setInterval(() => setClock((value) => value + 1), 1000);
        return () => window.clearInterval(interval);
    }, [running]);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            generationRef.current += 1;
            clearTimers();
            dismissToast();
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const start = async (input) => {
        if (busy || jobRef.current || !optionsRef.current.enabled) return;
        generationRef.current += 1;
        const ctx = context();
        setSubmitting(true);
        setError('');
        try {
            const response = await ctx.options.startJob(input);
            if (!current(ctx)) return;
            const nextJob = ctx.options.normalizeJob?.(response.data) || response.data?.job;
            if (!nextJob?.id || !nextJob?.status) throw new Error('A API não retornou um teste válido.');
            writeLoadTestSession(ctx.key, nextJob.id, null, ctx.options.compactReport);
            acceptJob(nextJob, true);
            if (terminal(nextJob)) await loadReport(nextJob, ctx);
            else schedulePoll(nextJob.id, ctx, 0);
        } catch (requestError) {
            if (current(ctx)) showError(requestError.response?.data?.message || requestError.message || 'Não foi possível iniciar o teste.');
        } finally {
            if (current(ctx)) setSubmitting(false);
        }
    };
    const cancel = async () => {
        if (!jobRef.current?.id || cancelling || !running) return;
        const ctx = context();
        const previousJob = jobRef.current;
        setCancelling(true);
        setError('');
        try {
            const response = await ctx.options.cancelJob(previousJob.id);
            if (!current(ctx)) return;
            const nextJob = ctx.options.normalizeJob?.(response.data) || response.data?.job || previousJob;
            if (String(nextJob.id) !== String(previousJob.id)) throw new Error('A API retornou outro teste ao solicitar o encerramento.');
            if (terminal(jobRef.current)) return;
            acceptJob(nextJob);
            if (terminal(nextJob)) {
                window.clearTimeout(pollTimerRef.current);
                pollTimerRef.current = null;
                setCancelling(false);
                await loadReport(nextJob, ctx);
            } else if (!pollRequestRef.current) schedulePoll(nextJob.id, ctx, 0);
        } catch (requestError) {
            if (current(ctx)) {
                setCancelling(false);
                showError(requestError.response?.data?.message || 'Não foi possível solicitar o encerramento.');
            }
        }
    };
    const reset = () => {
        if (busy) return false;
        generationRef.current += 1;
        clearTimers();
        reportRequestRef.current = null;
        pollRequestRef.current = null;
        jobRef.current = null;
        finalDataRef.current = null;
        setJob(null);
        setFinalData(null);
        setError('');
        setMonitoringPaused(false);
        setReportAttempt(0);
        signalRunning(false);
        removeLoadTestSession(optionsRef.current.sessionKey);
        dismissToast();
        return true;
    };
    const retry = () => {
        if (busy) return;
        setError('');
        if (terminal(jobRef.current)) loadReport(jobRef.current);
        else if (jobRef.current) {
            setMonitoringPaused(false);
            signalRunning(true);
            schedulePoll(jobRef.current.id, context(), 0);
        } else restore(context());
    };
    return { job, finalData, running, busy, stage, submitting, cancelling, loadingReport, restoringSession,
        reportAttempt, error, monitoringPaused, setError, start, cancel, reset, retry };
}
